import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../api/me';
import {
  DOWNLOAD_SIZE,
  createContactQrCode,
  getQrCodeContactPhoto,
  getQrCodeImage,
  listQrCodes,
  updateQrCodeContact,
} from '../api/qrCodes';
import type { QrCode, QrImageFormat, VCardContact, VCardPreview, VCardProfile } from '../api/types';
import {
  getEntraPhoto,
  getMyVCardProfile,
  getVCardProfileFor,
  previewVCard,
  renderVCardImage,
  searchVCardDirectory,
} from '../api/vcard';
import { useCurrentUser } from '../auth/CurrentUserProvider';
import { contactForForm, contactFromProfile, entraDifferences, sameCard, withProfile } from '../contactCard';
import { formatTimestamp } from '../format';
import { CopyButton } from '../components/CopyButton';
import { useDialog } from '../components/Dialog';
import { ErrorBanner } from '../components/ErrorBanner';
import { DEFAULT_IMAGE_STYLE, type ImageStyle } from '../components/ImageStyleFields';
import { useSetPageTitle } from '../components/PageTitle';
import { QrArtwork } from '../components/QrArtwork';
import { useToast } from '../components/Toast';
import { UserPicker, type UserSelection } from '../components/UserPicker';
import { VCardForm, contactValidationError } from '../components/VCardForm';
import { saveBlob } from '../download';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

type Subject = 'me' | 'someone-else';
type Output = 'short-link' | 'full';

const EMPTY: VCardContact = { firstName: '', lastName: '' };

/** Blank fields are omitted rather than sent as empty strings, which the API would have to reject. */
function forSubmission(contact: VCardContact): VCardContact {
  const trimmed = Object.entries(contact)
    .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value] as const)
    .filter(([, value]) => value !== '' && value !== null && value !== undefined);

  return {
    ...Object.fromEntries(trimmed),
    firstName: contact.firstName.trim(),
    lastName: contact.lastName.trim(),
  } as VCardContact;
}

function initials(contact: VCardContact): string {
  return `${contact.firstName.trim().charAt(0)}${contact.lastName.trim().charAt(0)}`.toUpperCase();
}

/** Holds the latest object URL and releases the one it replaces, so no caller has to remember to. */
function useObjectUrl(): [string | null, (blob: Blob | null) => void] {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    },
    [url],
  );

  const set = useCallback((blob: Blob | null) => setUrl(blob ? URL.createObjectURL(blob) : null), []);
  return [url, set];
}

/**
 * Builds a contact card and hands it out two ways. The short link, offered first, is a small link code pointing at
 * the card: the card can change later behind the same image, and each person has one. The full code carries the
 * card itself, so it never changes and needs no account to keep working. The form is on the left and everything it
 * produces is on the right, redrawn as the fields change, so there is no step between typing and seeing.
 */
export function VCardPage() {
  const { t } = useTranslation();
  const dialog = useDialog();
  const toast = useToast();
  const { user: currentUser, refresh: refreshCurrentUser } = useCurrentUser();
  useSetPageTitle(t('vcard.title'));

  const [subject, setSubject] = useState<Subject>('me');
  const [canPickOthers, setCanPickOthers] = useState(false);
  const [selection, setSelection] = useState<UserSelection | null>(null);

  const [contact, setContact] = useState<VCardContact>(EMPTY);
  const [profile, setProfile] = useState<VCardProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [output, setOutput] = useState<Output>('short-link');
  const [style, setStyle] = useState<ImageStyle>(DEFAULT_IMAGE_STYLE);
  const [myCodes, setMyCodes] = useState<QrCode[]>([]);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const [fullImageUrl, setFullImage] = useObjectUrl();
  const [shortImageUrl, setShortImage] = useObjectUrl();
  const [photoUrl, setPhoto] = useObjectUrl();
  const [entraPhotoUrl, setEntraPhoto] = useObjectUrl();
  const seededFrom = useRef<string | null>(null);
  const [preview, setPreview] = useState<VCardPreview | null>(null);
  const renderRequest = useRef(0);

  useEffect(() => {
    getCurrentUser()
      .then((user) => setCanPickOthers(user.isGroupMemberSomewhere))
      // Not being able to read /api/me only means the colleague half stays hidden; the page still works.
      .catch(() => setCanPickOthers(false));
  }, []);

  useEffect(() => {
    listQrCodes()
      .then(setMyCodes)
      // Only used to find the short link already made; without it the page simply offers to create one.
      .catch(() => setMyCodes([]));
  }, []);

  // A first load fills the form from Entra; a sync later only replaces what Entra holds, keeping the rest.
  const loadProfile = useCallback(async (userId: string | null, sync = false) => {
    setLoadingProfile(!sync);
    try {
      const loaded = userId === null ? await getMyVCardProfile() : await getVCardProfileFor(userId);
      setProfile(loaded);
      setContact((current) => (sync ? withProfile(current, loaded) : contactFromProfile(loaded)));
      setError(null);
    } catch (caught) {
      // The pre-fill is a convenience. If it fails outright, the form still opens and is typed in by hand.
      setProfile(null);
      setError(caught);
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  useEffect(() => {
    if (subject === 'me') {
      void loadProfile(null);
      return;
    }

    if (selection === null) {
      setProfile(null);
      setContact(EMPTY);
      setLoadingProfile(false);
      return;
    }

    void loadProfile(selection.userId);
  }, [subject, selection, loadProfile]);

  // Whose Entra profile the card mirrors. A card typed in by hand for nobody in particular mirrors none.
  const subjectId =
    subject === 'me' ? (profile?.userId ?? currentUser?.userId ?? null) : (selection?.userId ?? null);

  const existingLink = useMemo(
    () =>
      subjectId === null
        ? undefined
        : myCodes.find(
            (code) =>
              code.inPersonalSpace &&
              code.ownerGroupId === currentUser?.personalSpaceId &&
              !code.isArchived &&
              code.contactEntraUserId === subjectId,
          ),
    [myCodes, subjectId, currentUser?.personalSpaceId],
  );

  // An existing short link is what is printed, so the form opens on it rather than on Entra; Entra's values then
  // show beside the fields they differ on.
  useEffect(() => {
    if (loadingProfile || !existingLink?.contact || seededFrom.current === existingLink.id) {
      return;
    }

    seededFrom.current = existingLink.id;
    setContact(contactForForm(existingLink.contact));
  }, [existingLink, loadingProfile]);

  useEffect(() => {
    if (subjectId === null) {
      setEntraPhoto(null);
      return;
    }

    let cancelled = false;
    getEntraPhoto(subject === 'me' ? null : subjectId)
      .then((photo) => !cancelled && setEntraPhoto(photo))
      // No photo in Entra, or none readable: the card simply goes without.
      .catch(() => !cancelled && setEntraPhoto(null));
    return () => {
      cancelled = true;
    };
  }, [subject, subjectId, setEntraPhoto]);

  const differences = useMemo(
    () => (subjectId === null ? {} : entraDifferences(contact, profile)),
    [contact, profile, subjectId],
  );
  const matchesEntra = subjectId !== null && profile !== null && Object.keys(differences).length === 0;

  const payload = useMemo(() => forSubmission(contact), [contact]);
  const valid = contactValidationError(payload, t) === null;
  // The short link hands out what was last saved, so edits since then are worth saying out loud.
  const unsaved = existingLink?.contact != null && !sameCard(payload, contactForForm(existingLink.contact));
  const settled = useDebouncedValue(JSON.stringify(payload), 400);

  // The full code is the card itself, so it is redrawn from the fields - once the typing stops.
  useEffect(() => {
    if (output !== 'full') {
      return;
    }

    const card = JSON.parse(settled) as VCardContact;
    const request = ++renderRequest.current;
    if (contactValidationError(card, t) !== null) {
      setFullImage(null);
      setPreview(null);
      return;
    }

    Promise.all([
      renderVCardImage(card, 'png', 10, false, style.foreground, style.background, style.logoStyle),
      previewVCard(card),
    ])
      .then(([image, text]) => {
        if (request === renderRequest.current) {
          setFullImage(image);
          setPreview(text);
          setError(null);
        }
      })
      .catch((caught) => {
        if (request === renderRequest.current) {
          setError(caught);
        }
      });
  }, [output, settled, style, t, setFullImage]);

  useEffect(() => {
    if (!existingLink) {
      setShortImage(null);
      return;
    }

    let cancelled = false;
    getQrCodeImage(existingLink.id, 'png', 10, style.foreground, style.background, style.logoStyle)
      .then((image) => !cancelled && setShortImage(image))
      .catch((caught) => !cancelled && setError(caught));
    return () => {
      cancelled = true;
    };
  }, [existingLink, style, setShortImage]);

  useEffect(() => {
    if (!existingLink?.contactHasPhoto) {
      setPhoto(null);
      return;
    }

    let cancelled = false;
    getQrCodeContactPhoto(existingLink.id)
      .then((photo) => !cancelled && setPhoto(photo))
      .catch(() => !cancelled && setPhoto(null));
    return () => {
      cancelled = true;
    };
  }, [existingLink, setPhoto]);

  function changeSubject(next: Subject) {
    seededFrom.current = null;
    setSubject(next);
    setSelection(null);
    setActionError(null);
  }

  function replaceCode(code: QrCode) {
    setMyCodes((codes) => [code, ...codes.filter((existing) => existing.id !== code.id)]);
  }

  async function saveShortLink() {
    const problem = contactValidationError(payload, t);
    setActionError(problem);
    if (problem !== null) {
      return;
    }

    setSaving(true);
    try {
      if (existingLink) {
        replaceCode(await updateQrCodeContact(existingLink.id, payload, matchesEntra));
        toast.success(t('toast.contactSaved'));
      } else {
        replaceCode(await createContactQrCode(payload, subjectId, matchesEntra));
        refreshCurrentUser();
        toast.success(t('toast.contactCodeCreated'));
      }
      setError(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setSaving(false);
    }
  }

  async function download(format: QrImageFormat) {
    try {
      if (output === 'short-link' && existingLink) {
        const image = await getQrCodeImage(
          existingLink.id,
          format,
          DOWNLOAD_SIZE[format],
          style.foreground,
          style.background,
          style.logoStyle,
        );
        saveBlob(image, `qr-${existingLink.slug}.${format}`);
        return;
      }

      const image = await renderVCardImage(
        payload,
        format,
        DOWNLOAD_SIZE[format],
        true,
        style.foreground,
        style.background,
        style.logoStyle,
      );
      saveBlob(image, `vcard-${payload.firstName}-${payload.lastName}.${format}`.toLowerCase());
    } catch (caught) {
      // About the button just pressed, so it is answered in front of the user rather than in the page banner.
      await dialog.alert({
        title: t('common.notAvailable'),
        body: caught instanceof Error ? caught.message : String(caught),
        tone: 'danger',
      });
    }
  }

  const name = `${payload.firstName} ${payload.lastName}`.trim();
  const shown = (field: keyof VCardContact) => (payload.hiddenFields?.includes(field) ? undefined : payload[field]);
  const subtitle = [shown('title'), shown('organization')].filter(Boolean).join(' · ');
  const address = [
    shown('street'),
    [shown('postcode'), shown('city')].filter(Boolean).join(' '),
    shown('stateRegion'),
    shown('country'),
  ]
    .filter(Boolean)
    .join(', ');
  const previewPhoto = payload.includePhoto === false ? null : (entraPhotoUrl ?? photoUrl);

  return (
    <>
      <p className="hint">{t('vcard.intro')}</p>

      <ErrorBanner error={error} />

      <div className="vcard-layout">
        <section className="card vcard-layout__form" aria-labelledby="vcard-details-heading">
          <h2 id="vcard-details-heading">
            <i className="pi pi-user-edit me-2" aria-hidden="true" />
            {t('vcard.contactDetails')}
          </h2>

          {canPickOthers && (
            <div className="vcard-subject">
              <div className="segmented segmented--compact" role="radiogroup" aria-label={t('vcard.whoseCard')}>
                <input
                  id="vcard-subject-me"
                  type="radio"
                  className="visually-hidden"
                  name="vcard-subject"
                  checked={subject === 'me'}
                  onChange={() => changeSubject('me')}
                />
                <label htmlFor="vcard-subject-me" className={subject === 'me' ? 'is-selected' : undefined}>
                  {t('vcard.mine')}
                </label>
                <input
                  id="vcard-subject-other"
                  type="radio"
                  className="visually-hidden"
                  name="vcard-subject"
                  checked={subject === 'someone-else'}
                  onChange={() => changeSubject('someone-else')}
                />
                <label htmlFor="vcard-subject-other" className={subject === 'someone-else' ? 'is-selected' : undefined}>
                  {t('vcard.somebodyElse')}
                </label>
              </div>

              {subject === 'someone-else' && (
                <div className="vcard-subject__picker">
                  <UserPicker
                    compact
                    selection={selection}
                    onChange={setSelection}
                    disabled={saving}
                    search={searchVCardDirectory}
                    // The object id is of no use here: the search only pre-fills fields that can be typed instead.
                    allowManualId={false}
                  />
                </div>
              )}
            </div>
          )}

          {profile?.warning && (
            <div className="alert alert-info d-flex gap-2" role="status">
              <i className="pi pi-info-circle mt-1" aria-hidden="true" />
              <span>{profile.warning}</span>
            </div>
          )}

          {existingLink?.contactEntraSyncedAt && (
            <p className="hint">
              {t('vcard.lastSynced', { date: formatTimestamp(existingLink.contactEntraSyncedAt) })}
            </p>
          )}

          {subject === 'someone-else' && selection === null && (
            <p className="hint">{t('vcard.waitingForSelection')}</p>
          )}

          {loadingProfile ? (
            <p>
              <i className="pi pi-spin pi-spinner me-2" aria-hidden="true" />
              {t('vcard.loadingProfile')}
            </p>
          ) : (
            <VCardForm
              values={contact}
              onChange={(next) => {
                setContact(next);
                setActionError(null);
              }}
              submitting={saving}
              collapseExtras
              hideable
              photoChoice={subjectId !== null}
              entraValues={differences}
              onRefill={subjectId === null ? undefined : () => void loadProfile(subject === 'me' ? null : subjectId, true)}
              refillLabel={t('qrCode.contact.sync')}
            />
          )}

          {unsaved && (
            <div className="vcard-unsaved" role="status">
              <span>
                <i className="pi pi-info-circle me-2" aria-hidden="true" />
                {t('vcard.shortLink.unsaved')}
              </span>
              <button type="button" className="btn btn-primary" disabled={saving || !valid} onClick={() => void saveShortLink()}>
                <i className={`pi ${saving ? 'pi-spin pi-spinner' : 'pi-check'} me-2`} aria-hidden="true" />
                {saving ? t('vcard.shortLink.updating') : t('vcard.shortLink.update')}
              </button>
            </div>
          )}
        </section>

        <aside className="vcard-layout__preview" aria-label={t('vcard.preview')}>
          <div className="card contact-preview">
            {previewPhoto ? (
              <img className="contact-preview__photo" src={previewPhoto} alt={t('qrCode.contact.photoAlt')} />
            ) : (
              <span className="contact-preview__photo contact-preview__initials" aria-hidden="true">
                {initials(payload) || <i className="pi pi-user" />}
              </span>
            )}
            <div className="contact-preview__body">
              <p className="contact-preview__name">{name || t('vcard.noName')}</p>
              {subtitle && <p className="contact-preview__subtitle">{subtitle}</p>}
            </div>
            <ul className="contact-preview__lines">
              {shown('email') && (
                <li>
                  <i className="pi pi-envelope" aria-hidden="true" />
                  {shown('email')}
                </li>
              )}
              {shown('mobile') && (
                <li>
                  <i className="pi pi-mobile" aria-hidden="true" />
                  {shown('mobile')}
                </li>
              )}
              {shown('workPhone') && (
                <li>
                  <i className="pi pi-phone" aria-hidden="true" />
                  {shown('workPhone')}
                </li>
              )}
              {shown('website') && (
                <li>
                  <i className="pi pi-globe" aria-hidden="true" />
                  {(shown('website') as string).replace(/^https?:\/\/(www\.)?/, '')}
                </li>
              )}
              {shown('linkedin') && (
                <li>
                  <i className="pi pi-linkedin" aria-hidden="true" />
                  {(shown('linkedin') as string).replace(/^https?:\/\/(www\.)?/, '')}
                </li>
              )}
              {address && (
                <li>
                  <i className="pi pi-map-marker" aria-hidden="true" />
                  {address}
                </li>
              )}
              {shown('note') && <li className="contact-preview__note">{shown('note')}</li>}
            </ul>
          </div>

          <div className="card">
            <div className="segmented" role="tablist" aria-label={t('vcard.outputLabel')}>
              <button
                type="button"
                role="tab"
                aria-selected={output === 'short-link'}
                className={output === 'short-link' ? 'is-selected' : undefined}
                onClick={() => setOutput('short-link')}
              >
                <i className="pi pi-link me-2" aria-hidden="true" />
                {t('vcard.shortLink.title')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={output === 'full'}
                className={output === 'full' ? 'is-selected' : undefined}
                onClick={() => setOutput('full')}
              >
                <i className="pi pi-qrcode me-2" aria-hidden="true" />
                {t('vcard.full.title')}
              </button>
            </div>

            {actionError && (
              <div className="alert alert-danger d-flex gap-2 mt-3" role="alert">
                <i className="pi pi-exclamation-circle mt-1" aria-hidden="true" />
                <span>{actionError}</span>
              </div>
            )}

            <div role="tabpanel" className="vcard-output">
              {output === 'short-link' ? (
                existingLink ? (
                  <>
                    <QrArtwork
                      idPrefix="vcard-short"
                      imageUrl={shortImageUrl}
                      alt={t('vcard.shortLink.imageAlt', { name: existingLink.title })}
                      loadingLabel={t('qrCode.rendering')}
                      transparentHint={t('qrCode.backgroundTransparentHint')}
                      style={style}
                      onStyleChange={setStyle}
                    />
                    <p className="link-meta__inline">
                      <span className="mono text-truncate">{existingLink.redirectUrl}</span>
                      <CopyButton value={existingLink.redirectUrl} />
                    </p>
                    <p className="hint">{t('vcard.shortLink.updateHint')}</p>
                    <div className="form-actions">
                      <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void saveShortLink()}>
                        <i className={`pi ${saving ? 'pi-spin pi-spinner' : 'pi-check'} me-2`} aria-hidden="true" />
                        {saving ? t('vcard.shortLink.updating') : t('vcard.shortLink.update')}
                      </button>
                      <Link to={`/qr/${existingLink.id}`} className="btn btn-light">
                        <i className="pi pi-external-link me-2" aria-hidden="true" />
                        {t('vcard.shortLink.open')}
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="hint">{t('vcard.shortLink.hint')}</p>
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={saving || !valid}
                        onClick={() => void saveShortLink()}
                      >
                        <i className={`pi ${saving ? 'pi-spin pi-spinner' : 'pi-link'} me-2`} aria-hidden="true" />
                        {saving ? t('vcard.shortLink.creating') : t('vcard.shortLink.create')}
                      </button>
                    </div>
                  </>
                )
              ) : valid ? (
                <>
                  <QrArtwork
                    idPrefix="vcard"
                    imageUrl={fullImageUrl}
                    alt={t('vcard.imageAlt', { name })}
                    loadingLabel={t('qrCode.rendering')}
                    transparentHint={t('qrCode.backgroundTransparentHint')}
                    style={style}
                    onStyleChange={setStyle}
                  />
                  {preview && (
                    <>
                      <p className="hint mb-1">
                        {t('vcard.payloadUsage', { used: preview.byteCount, max: preview.maxByteCount })}
                      </p>
                      <details className="vcard-encoded">
                        <summary>{t('vcard.encodedCard')}</summary>
                        <pre className="mono vcard-text">{preview.content}</pre>
                        <CopyButton value={preview.content} />
                      </details>
                    </>
                  )}
                  <p className="hint">{t('vcard.full.hint')}</p>
                </>
              ) : (
                <p className="hint">{t('vcard.full.needsName')}</p>
              )}

              {((output === 'short-link' && existingLink) || (output === 'full' && valid)) && (
                <div className="form-actions">
                  <button type="button" className="btn btn-light" onClick={() => void download('png')}>
                    <i className="pi pi-image me-2" aria-hidden="true" />
                    {t('common.downloadPng')}
                  </button>
                  <button type="button" className="btn btn-light" onClick={() => void download('svg')}>
                    <i className="pi pi-file me-2" aria-hidden="true" />
                    {t('common.downloadSvg')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
